import React from 'react';
import { LazyTooltip } from '../../design-system/Tooltip';
import { Icon } from '../../design-system/icons/Icon';
import type { ViewMode } from '../../pages/index/hooks/useViewMode';
import type { useFormatMessage } from '../../i18n-review/client/Msg';

/* List/map view toggle, matches the flat white/bordered pill chrome of the sort dropdown and
 * feedback button beside it (see SRP_INLINE_SELECT_CLASSNAME), instead of the taller, filled
 * beige segmented-control look used for the sort segments, so it reads as part of the same
 * control row rather than a bulkier, differently-styled element dropped into it. Extracted out of
 * SearchSidebar.tsx so it can also render floating over the map itself on mobile (see MapView.tsx's
 * `mapOverlay` prop / SrpView.tsx), not just in the toolbar's normal document flow. */
export const VIEW_TOGGLE_GROUP_CLASS =
  'inline-flex items-center gap-0.5 flex-none rounded-lg border border-[#DDD5C8] dark:border-[#4A3520] bg-white dark:bg-[#1F1509] p-1 shadow-sm dark:shadow-none';

export const VIEW_TOGGLE_BTN_BASE =
  'flex h-7 w-7 items-center justify-center rounded-md transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-[#3B5E45] dark:focus:ring-[#98CFA8] cursor-pointer';

export const VIEW_TOGGLE_BTN_ACTIVE = 'bg-[#3B5E45] dark:bg-[#98CFA8] text-white dark:text-[#16150F]';

export const VIEW_TOGGLE_BTN_INACTIVE =
  'bg-transparent text-[#8C8474] hover:bg-[#F2EADF] dark:hover:bg-[#342415] hover:text-[#3D3529] dark:hover:text-[#C4A882]';

export interface MapViewModeToggleProps {
  viewMode: ViewMode;
  onSelectList: () => void;
  onSelectMap: () => void;
  formatMessage: ReturnType<typeof useFormatMessage>;
  /** Extra classes on the outer pill (e.g. a drop shadow when floating over the map). */
  className?: string;
}

export function MapViewModeToggle({ viewMode, onSelectList, onSelectMap, formatMessage, className = '' }: MapViewModeToggleProps) {
  return (
    <div className={`${VIEW_TOGGLE_GROUP_CLASS} ${className}`}>
      {/* Tooltip describes the switch-to action only while inactive: while a view is already
          showing, its own button's tooltip just names it instead of instructing you to
          "switch to" what's already on screen. */}
      <LazyTooltip
        content={
          viewMode === 'list'
            ? formatMessage({
                id: 'view.list',
                defaultMessage: 'List view',
                description: 'Button aria-label to switch to list view layout',
              })
            : formatMessage({
                id: 'tooltip.view.list',
                defaultMessage: 'Switch to list view to see jobs in a compact list format',
                description: 'Tooltip text for list view button',
              })
        }
        position="bottom"
      >
        <button
          type="button"
          className={`${VIEW_TOGGLE_BTN_BASE} ${viewMode === 'list' ? VIEW_TOGGLE_BTN_ACTIVE : VIEW_TOGGLE_BTN_INACTIVE}`}
          onClick={onSelectList}
          aria-pressed={viewMode === 'list'}
          aria-label={formatMessage({
            id: 'view.list',
            defaultMessage: 'List view',
            description: 'Button aria-label to switch to list view layout',
          })}
        >
          <Icon name="bars4" useMaskForColor className="h-4 w-4" />
        </button>
      </LazyTooltip>
      <LazyTooltip
        content={
          viewMode === 'map'
            ? formatMessage({
                id: 'view.map',
                defaultMessage: 'Map view',
                description: 'Button aria-label to switch to map view layout',
              })
            : formatMessage({
                id: 'tooltip.view.map',
                defaultMessage: 'Switch to map view to see job locations on a map',
                description: 'Tooltip text for map view button',
              })
        }
        position="bottom"
      >
        <button
          type="button"
          className={`${VIEW_TOGGLE_BTN_BASE} ${viewMode === 'map' ? VIEW_TOGGLE_BTN_ACTIVE : VIEW_TOGGLE_BTN_INACTIVE}`}
          onClick={onSelectMap}
          aria-pressed={viewMode === 'map'}
          aria-label={formatMessage({
            id: 'view.map',
            defaultMessage: 'Map view',
            description: 'Button aria-label to switch to map view layout',
          })}
        >
          <Icon name="mapPin" useMaskForColor className="h-4 w-4" />
        </button>
      </LazyTooltip>
    </div>
  );
}
